import type { Variant } from "@raumagarn/formatters";
import { useMemo, useReducer } from "react";

export interface MappedColor {
	color: string;
	image: string;
	yarn: Variant[];
	selected: boolean;
	soldOut?: boolean;
}

export type ProductWithQuantity = Variant & {
	quantity: number;
	recommendedSize: number;
};

interface VariantPickerState {
	selectedColor: MappedColor | null;
	yarnQuantities: Record<string, number>;
	customYarns: Record<string, Variant>;
	currentEditingYarn: Variant | null;
	selectedYarnInDialog: Variant | null;
}

type VariantPickerAction =
	| { type: "SET_SELECTED_COLOR"; payload: MappedColor | null }
	| { type: "SET_YARN_QUANTITY"; payload: { itemId: string; quantity: number } }
	| { type: "SET_CUSTOM_YARN"; payload: { itemId: string; yarn: Variant } }
	| {
			type: "START_EDITING_YARN";
			payload: { yarn: Variant; currentCustom?: Variant };
	  }
	| { type: "SET_DIALOG_SELECTION"; payload: Variant }
	| { type: "SAVE_YARN_SELECTION" }
	| { type: "CANCEL_YARN_EDITING" }
	| { type: "RESET_ALL" };

const initialState: VariantPickerState = {
	selectedColor: null,
	yarnQuantities: {},
	customYarns: {},
	currentEditingYarn: null,
	selectedYarnInDialog: null,
};

const variantPickerReducer = (
	state: VariantPickerState,
	action: VariantPickerAction,
): VariantPickerState => {
	switch (action.type) {
		case "SET_SELECTED_COLOR":
			return { ...state, selectedColor: action.payload };

		case "SET_YARN_QUANTITY":
			return {
				...state,
				yarnQuantities: {
					...state.yarnQuantities,
					[action.payload.itemId]: Math.max(action.payload.quantity, 0),
				},
			};

		case "SET_CUSTOM_YARN":
			return {
				...state,
				customYarns: {
					...state.customYarns,
					[action.payload.itemId]: action.payload.yarn,
				},
			};

		case "START_EDITING_YARN":
			return {
				...state,
				currentEditingYarn: action.payload.yarn,
				selectedYarnInDialog: action.payload.currentCustom || action.payload.yarn,
			};

		case "SET_DIALOG_SELECTION":
			return {
				...state,
				selectedYarnInDialog: action.payload,
			};

		case "SAVE_YARN_SELECTION": {
			if (!state.currentEditingYarn || !state.selectedYarnInDialog) return state;
			const slotItem = state.currentEditingYarn.size?.item!;
			// The real variant's own item id - this is what must actually be sent
			// to Centra when adding to cart. It gets clobbered below by the slot's
			// stable id, so capture it first under `centraItemId`.
			const realItemId = state.selectedYarnInDialog.size?.item;
			// Keep the slot's own identity (item id, displayItemId) so quantity
			// keying and slot lookups stay stable after swapping in a real variant.
			const normalizedSelection: Variant & { centraItemId?: string } = {
				...state.selectedYarnInDialog,
				displayItemId: state.currentEditingYarn.displayItemId,
				centraItemId: realItemId,
				size: {
					...state.selectedYarnInDialog.size!,
					item: slotItem,
				},
			};
			return {
				...state,
				customYarns: {
					...state.customYarns,
					[slotItem]: normalizedSelection,
				},
				currentEditingYarn: null,
				selectedYarnInDialog: null,
			};
		}

		case "CANCEL_YARN_EDITING":
			return {
				...state,
				currentEditingYarn: null,
				selectedYarnInDialog: null,
			};

		case "RESET_ALL":
			return initialState;

		default:
			return state;
	}
};

export const useVariantPickerState = () => {
	const [state, dispatch] = useReducer(variantPickerReducer, initialState);

	const actions = useMemo(
		() => ({
			setSelectedColor: (color: MappedColor | null) =>
				dispatch({ type: "SET_SELECTED_COLOR", payload: color }),

			setYarnQuantity: (itemId: string, quantity: number) =>
				dispatch({ type: "SET_YARN_QUANTITY", payload: { itemId, quantity } }),

			incrementYarnQuantity: (itemId: string, current: number, recommended: number) =>
				dispatch({
					type: "SET_YARN_QUANTITY",
					payload: { itemId, quantity: (current || recommended) + 1 },
				}),

			decrementYarnQuantity: (itemId: string, current: number, recommended: number) =>
				dispatch({
					type: "SET_YARN_QUANTITY",
					payload: {
						itemId,
						quantity: Math.max((current || recommended) - 1, 0),
					},
				}),

			startEditingYarn: (yarn: Variant, customYarns: Record<string, Variant>) =>
				dispatch({
					type: "START_EDITING_YARN",
					payload: { yarn, currentCustom: customYarns[yarn.size?.item!] },
				}),

			selectYarnInDialog: (yarn: Variant) =>
				dispatch({ type: "SET_DIALOG_SELECTION", payload: yarn }),

			saveYarnSelection: () => dispatch({ type: "SAVE_YARN_SELECTION" }),

			cancelYarnEditing: () => dispatch({ type: "CANCEL_YARN_EDITING" }),

			resetAll: () => dispatch({ type: "RESET_ALL" }),
		}),
		[],
	);

	const derived = useMemo(
		() => ({
			// Helper to get final yarn with custom overrides and quantities
			getYarnWithState: (yarn: Variant, index: number, selectedSize: any) =>
				({
					...(state.customYarns[yarn.size?.item!] || yarn),
					quantity: state.yarnQuantities[yarn.size?.item!] || selectedSize?.yarn[index] || 0,
					recommendedSize: selectedSize?.yarn[index] || 0,
				}) as ProductWithQuantity,

			// Helper to build yarns array
			buildYarnsArray: (
				selectedColor: MappedColor | null,
				selectedSize: any,
			): ProductWithQuantity[] =>
				selectedColor?.yarn.map((yarn, i) => derived.getYarnWithState(yarn, i, selectedSize)) || [],
		}),
		[state.customYarns, state.yarnQuantities],
	);

	return {
		state,
		actions,
		derived,
	};
};
